import { useState } from 'react';
import { Plus, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import type { CommissionPlanConfig, CommissionTier } from '@trinco/shared';

type PlanType = CommissionPlanConfig['type'];

interface PlanEditorProps {
  plan?: any;
  onSave: () => void;
  onCancel: () => void;
}

const PLAN_TYPE_LABELS: Record<PlanType, string> = {
  flat: 'Flat Rate',
  tiered: 'Tiered',
  team_quota: 'Team Quota',
  accelerator: 'Accelerator',
};

export function PlanEditor({ plan, onSave, onCancel }: PlanEditorProps) {
  const [name, setName] = useState(plan?.name ?? '');
  const [type, setType] = useState<PlanType>(plan?.config?.type ?? 'flat');
  const [flatRate, setFlatRate] = useState(
    plan?.config?.type === 'flat' ? String(plan.config.rate * 100) : '10',
  );
  const [tiers, setTiers] = useState<CommissionTier[]>(
    plan?.config?.tiers ?? [
      { name: 'Base', minRevenue: 0, maxRevenue: 50000, rate: 0.05, isIncremental: true },
      { name: 'Growth', minRevenue: 50000, maxRevenue: 100000, rate: 0.08, isIncremental: true },
      { name: 'Accelerator', minRevenue: 100000, maxRevenue: null, rate: 0.12, isIncremental: true },
    ],
  );
  const [baseRate, setBaseRate] = useState(
    plan?.config?.type === 'accelerator' ? String(plan.config.baseRate * 100) : '5',
  );
  const [quota, setQuota] = useState(
    plan?.config?.quota?.toString() ?? '100000',
  );
  const [teamQuota, setTeamQuota] = useState(
    plan?.config?.teamQuota?.toString() ?? '500000',
  );
  const [individualBase, setIndividualBase] = useState(
    plan?.config?.individualBase ? String(plan.config.individualBase * 100) : '5',
  );
  const [teamBonus, setTeamBonus] = useState(
    plan?.config?.teamBonus ? String(plan.config.teamBonus * 100) : '2',
  );
  const [saving, setSaving] = useState(false);

  const addTier = () => {
    const lastTier = tiers[tiers.length - 1];
    setTiers([
      ...tiers,
      {
        name: `Tier ${tiers.length + 1}`,
        minRevenue: lastTier?.maxRevenue ?? 0,
        maxRevenue: null,
        rate: (lastTier?.rate ?? 0.05) + 0.02,
        isIncremental: true,
      },
    ]);
  };

  const removeTier = (index: number) => {
    setTiers(tiers.filter((_, i) => i !== index));
  };

  const updateTier = (index: number, field: keyof CommissionTier, value: any) => {
    setTiers(tiers.map((t, i) => (i === index ? { ...t, [field]: value } : t)));
  };

  const handleSave = async () => {
    setSaving(true);
    // Will be wired to trpc.commission.createPlan / updatePlan
    await new Promise((r) => setTimeout(r, 300));
    setSaving(false);
    onSave();
  };

  return (
    <div className="space-y-4">
      <div className="space-y-2">
        <Label>Plan name</Label>
        <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Standard Sales Plan" />
      </div>

      <div className="space-y-2">
        <Label>Plan type</Label>
        <Select value={type} onValueChange={(v) => setType(v as PlanType)}>
          <SelectTrigger>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {(Object.entries(PLAN_TYPE_LABELS) as [PlanType, string][]).map(([key, label]) => (
              <SelectItem key={key} value={key}>{label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {type === 'flat' && (
        <div className="space-y-2">
          <Label>Commission rate (%)</Label>
          <Input type="number" step="0.1" value={flatRate} onChange={(e) => setFlatRate(e.target.value)} />
        </div>
      )}

      {(type === 'tiered' || type === 'team_quota') && (
        <div className="space-y-3">
          <Label>Tiers</Label>
          {tiers.map((tier, i) => (
            <div key={i} className="flex items-center gap-2 rounded-lg border p-3">
              <Input className="w-28" value={tier.name} onChange={(e) => updateTier(i, 'name', e.target.value)} placeholder="Name" />
              <Input className="w-24" type="number" value={tier.minRevenue} onChange={(e) => updateTier(i, 'minRevenue', Number(e.target.value))} placeholder="Min" />
              <span className="text-sm text-muted-foreground">to</span>
              <Input className="w-24" type="number" value={tier.maxRevenue ?? ''} onChange={(e) => updateTier(i, 'maxRevenue', e.target.value ? Number(e.target.value) : null)} placeholder="Max" />
              <Input className="w-20" type="number" step="0.1" value={(tier.rate * 100).toFixed(1)} onChange={(e) => updateTier(i, 'rate', Number(e.target.value) / 100)} />
              <span className="text-sm text-muted-foreground">%</span>
              <Button variant="ghost" size="icon" onClick={() => removeTier(i)}>
                <Trash2 className="h-4 w-4 text-muted-foreground" />
              </Button>
            </div>
          ))}
          <Button variant="outline" size="sm" onClick={addTier}>
            <Plus className="mr-1 h-4 w-4" /> Add tier
          </Button>
        </div>
      )}

      {type === 'team_quota' && (
        <div className="grid gap-4 sm:grid-cols-3">
          <div className="space-y-2">
            <Label>Team quota ($)</Label>
            <Input type="number" value={teamQuota} onChange={(e) => setTeamQuota(e.target.value)} />
          </div>
          <div className="space-y-2">
            <Label>Individual base (%)</Label>
            <Input type="number" step="0.1" value={individualBase} onChange={(e) => setIndividualBase(e.target.value)} />
          </div>
          <div className="space-y-2">
            <Label>Team bonus (%)</Label>
            <Input type="number" step="0.1" value={teamBonus} onChange={(e) => setTeamBonus(e.target.value)} />
          </div>
        </div>
      )}

      {type === 'accelerator' && (
        <div className="grid gap-4 sm:grid-cols-2">
          <div className="space-y-2">
            <Label>Base rate (%)</Label>
            <Input type="number" step="0.1" value={baseRate} onChange={(e) => setBaseRate(e.target.value)} />
          </div>
          <div className="space-y-2">
            <Label>Quota ($)</Label>
            <Input type="number" value={quota} onChange={(e) => setQuota(e.target.value)} />
          </div>
        </div>
      )}

      <div className="flex justify-end gap-3 pt-4">
        <Button variant="outline" onClick={onCancel}>Cancel</Button>
        <Button onClick={handleSave} disabled={saving || !name.trim()}>
          {saving ? 'Saving...' : plan ? 'Update plan' : 'Create plan'}
        </Button>
      </div>
    </div>
  );
}
